import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Checkbox,
  ListItemText,
  TablePagination,
  CircularProgress,
  Grid,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  Search,
  Visibility,
  Payment,
  Refresh,
  ViewColumn,
  AccountBalance,
  TrendingDown,
  Warning,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';
import { cleanupOrphanedCreditors } from '../firebase/creditorService';
import { CreditorViewDialog, CreditorPaymentDialog } from './CreditorDetail';
import useFirestoreStore from '../stores/useFirestoreStore';

const fmt = (n) =>
  (n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Creditors = ({ currentUser }) => {
  // Read from global store — no extra listeners
  const creditors = useFirestoreStore((state) => state.creditors);
  const loading = useFirestoreStore((state) => state.loading.creditors);

  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [showSettled, setShowSettled] = useState(true);
  const [selectedCreditor, setSelectedCreditor] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Column visibility
  const [columnAnchor, setColumnAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    index: true,
    customerName: true,
    phone: true,
    email: false,
    category: true,
    bills: true,
    dueDate: false,
    totalCredited: true,
    totalPaid: true,
    balance: true,
    status: true,
    actions: true,
  });

  // Auto-cleanup orphaned creditors
  useEffect(() => {
    const cleanup = async () => {
      await new Promise(r => setTimeout(r, 2000));
      await cleanupOrphanedCreditors();
    };
    cleanup();
  }, []);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => { setRefreshing(false); showSnackbar('Data is up to date'); }, 500);
  };

  const handleViewDetails = (creditor) => {
    setSelectedCreditor(creditor);
    setShowViewDialog(true);
  };

  const handleOpenPayment = (creditor) => {
    setSelectedCreditor(creditor);
    setShowPaymentDialog(true);
  };

  // ── Derived summary stats (all creditors, not filtered) ──
  const summary = useMemo(() => {
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    let totalOutstanding = 0, totalOverdue = 0, dueThisWeek = 0, pendingCount = 0;

    creditors.forEach(c => {
      if (c.status === 'Settled') return;
      const bal = c.balance || 0;
      if (bal <= 0) return;
      totalOutstanding += bal;
      pendingCount++;
      if (c.dueDate) {
        const due = new Date(c.dueDate);
        if (due < now) totalOverdue += bal;
        else if (due <= weekFromNow) dueThisWeek += bal;
      }
    });

    const totalPaidAll = creditors.reduce((s, c) => s + (c.totalPaid || 0), 0);
    return { totalOutstanding, totalOverdue, dueThisWeek, pendingCount, totalPaidAll };
  }, [creditors]);

  // ── Filtering ──
  const filteredCreditors = useMemo(() => {
    return creditors.filter(c => {
      const matchesSearch =
        c.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.customerPhone && c.customerPhone.includes(searchTerm)) ||
        (c.customerEmail && c.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = statusFilter === 'All' || c.status === statusFilter;
      const matchesCategory = categoryFilter === 'All' || c.category === categoryFilter;
      const matchesSettled = showSettled || c.status !== 'Settled';

      return matchesSearch && matchesStatus && matchesCategory && matchesSettled;
    });
  }, [creditors, searchTerm, statusFilter, categoryFilter, showSettled]);

  const paginatedCreditors = filteredCreditors.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Totals row
  const totals = useMemo(() => filteredCreditors.reduce(
    (acc, c) => ({
      totalCredited: acc.totalCredited + (c.totalCredited || 0),
      totalPaid: acc.totalPaid + (c.totalPaid || 0),
      balance: acc.balance + (c.balance || 0),
    }),
    { totalCredited: 0, totalPaid: 0, balance: 0 }
  ), [filteredCreditors]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Settled': return 'success';
      case 'Active': return 'warning';
      case 'Overdue': return 'error';
      default: return 'default';
    }
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'Koko': return 'warning';
      case 'MintPay': return 'warning';
      case 'COD': return 'info';
      case 'Credit': return 'primary';
      default: return 'default';
    }
  };

  const isOverdue = (c) =>
    c.dueDate && new Date(c.dueDate) < new Date() && c.status === 'Active';

  const colCount = Object.values(visibleColumns).filter(Boolean).length;

  const StatCard = ({ icon, value, label, color, subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
            {icon}
          </Box>
        </Box>
        <Typography variant="h5" fontWeight={700} color={color || 'text.primary'} gutterBottom>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={50} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={400} height={30} sx={{ mb: 3 }} />
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card><CardContent><Skeleton variant="rectangular" height={80} /></CardContent></Card>
            </Grid>
          ))}
        </Grid>
        <Card><CardContent><Skeleton variant="rectangular" height={400} /></CardContent></Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccountBalance sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Receivables
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track and manage credit payments from customers
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingDown />}
            value={`LKR ${fmt(summary.totalOutstanding)}`}
            label="Total Outstanding"
            color="error.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Warning />}
            value={`LKR ${fmt(summary.totalOverdue)}`}
            label="Overdue Amount"
            color={summary.totalOverdue > 0 ? 'error.main' : 'text.primary'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Schedule />}
            value={`LKR ${fmt(summary.dueThisWeek)}`}
            label="Due This Week"
            color={summary.dueThisWeek > 0 ? 'warning.main' : 'text.primary'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CheckCircle />}
            value={`LKR ${fmt(summary.totalPaidAll)}`}
            label="Total Collected"
            color="success.main"
          />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          {/* Toolbar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              All Receivables
              <Chip label={filteredCreditors.length} size="small" sx={{ ml: 1, fontWeight: 700 }} />
              {summary.pendingCount > 0 && (
                <Chip label={`${summary.pendingCount} pending`} size="small" color="warning" sx={{ ml: 1 }} />
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" size="small" startIcon={<ViewColumn />} onClick={e => setColumnAnchor(e.currentTarget)}>
                Columns
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, phone or email..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Overdue">Overdue</MenuItem>
                  <MenuItem value="Settled">Settled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={categoryFilter} label="Category" onChange={e => { setCategoryFilter(e.target.value); setPage(0); }}>
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Credit">Credit</MenuItem>
                  <MenuItem value="Koko">Koko</MenuItem>
                  <MenuItem value="MintPay">MintPay</MenuItem>
                  <MenuItem value="COD">COD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={showSettled}
                  onChange={e => setShowSettled(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">Show Settled</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {visibleColumns.index && <TableCell width={40}><strong>#</strong></TableCell>}
                  {visibleColumns.customerName && <TableCell><strong>Customer</strong></TableCell>}
                  {visibleColumns.phone && <TableCell><strong>Phone</strong></TableCell>}
                  {visibleColumns.email && <TableCell><strong>Email</strong></TableCell>}
                  {visibleColumns.category && <TableCell><strong>Type</strong></TableCell>}
                  {visibleColumns.bills && <TableCell align="center"><strong>Bills</strong></TableCell>}
                  {visibleColumns.dueDate && <TableCell><strong>Due Date</strong></TableCell>}
                  {visibleColumns.totalCredited && <TableCell align="right"><strong>Credited (LKR)</strong></TableCell>}
                  {visibleColumns.totalPaid && <TableCell align="right"><strong>Paid (LKR)</strong></TableCell>}
                  {visibleColumns.balance && <TableCell align="right"><strong>Balance (LKR)</strong></TableCell>}
                  {visibleColumns.status && <TableCell><strong>Status</strong></TableCell>}
                  {visibleColumns.actions && <TableCell align="center"><strong>Actions</strong></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCreditors.map((creditor, index) => {
                  const overdue = isOverdue(creditor);
                  return (
                    <TableRow
                      key={creditor.id}
                      hover
                      sx={{
                        backgroundColor: overdue ? 'rgba(211,47,47,0.05)' : 'inherit',
                        '&:hover': { backgroundColor: overdue ? 'rgba(211,47,47,0.1)' : 'action.hover' },
                      }}
                    >
                      {visibleColumns.index && (
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      )}
                      {visibleColumns.customerName && (
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{creditor.customerName}</Typography>
                          {creditor.customerEmail && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {creditor.customerEmail}
                            </Typography>
                          )}
                          {creditor.dueDate && creditor.status !== 'Settled' && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={overdue ? `Due: ${creditor.dueDate}` : `Due: ${creditor.dueDate}`}
                                size="small"
                                color={overdue ? 'error' : 'info'}
                                variant={overdue ? 'filled' : 'outlined'}
                                sx={{ fontSize: '0.68rem', height: 20 }}
                              />
                            </Box>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.phone && (
                        <TableCell>
                          {creditor.customerPhone ? (
                            <Box
                              component="a"
                              href={`tel:${creditor.customerPhone}`}
                              sx={{ color: 'info.main', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                            >
                              {creditor.customerPhone}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.email && (
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{creditor.customerEmail || '—'}</Typography>
                        </TableCell>
                      )}
                      {visibleColumns.category && (
                        <TableCell>
                          {creditor.category ? (
                            <Chip
                              label={creditor.category}
                              size="small"
                              color={getCategoryColor(creditor.category)}
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.bills && (
                        <TableCell align="center">
                          <Tooltip title="View bills & history">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewDetails(creditor)}
                              sx={{ fontSize: '0.72rem', textTransform: 'none', minWidth: 60 }}
                            >
                              {creditor.bills?.length || 0} bill{(creditor.bills?.length || 0) !== 1 ? 's' : ''}
                            </Button>
                          </Tooltip>
                        </TableCell>
                      )}
                      {visibleColumns.dueDate && (
                        <TableCell>
                          {creditor.dueDate ? (
                            <Box>
                              <Typography
                                variant="body2"
                                color={overdue ? 'error.main' : 'text.primary'}
                                fontWeight={overdue ? 700 : 400}
                              >
                                {creditor.dueDate}
                              </Typography>
                              {overdue && (
                                <Typography variant="caption" color="error.main">Overdue</Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.totalCredited && (
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>{fmt(creditor.totalCredited)}</Typography>
                        </TableCell>
                      )}
                      {visibleColumns.totalPaid && (
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main" fontWeight={600}>{fmt(creditor.totalPaid)}</Typography>
                        </TableCell>
                      )}
                      {visibleColumns.balance && (
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={creditor.balance > 0 ? 'error.main' : 'success.main'}
                          >
                            {fmt(creditor.balance)}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell>
                          <Chip label={creditor.status} size="small" color={getStatusColor(creditor.status)} />
                        </TableCell>
                      )}
                      {visibleColumns.actions && (
                        <TableCell align="center">
                          <Tooltip title="View details & history">
                            <IconButton size="small" color="primary" onClick={() => handleViewDetails(creditor)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {creditor.status !== 'Settled' && (
                            <Tooltip title="Record payment">
                              <IconButton size="small" color="success" onClick={() => handleOpenPayment(creditor)}>
                                <Payment fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}

                {paginatedCreditors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        {searchTerm || statusFilter !== 'All' || categoryFilter !== 'All'
                          ? 'No records match your filters'
                          : 'No receivables yet. Credit/COD/Koko/MintPay bills will appear here.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {/* Totals row */}
                {filteredCreditors.length > 0 && (
                  <TableRow sx={{ backgroundColor: 'action.selected', borderTop: '2px solid', borderTopColor: 'divider' }}>
                    {visibleColumns.index && <TableCell />}
                    {visibleColumns.customerName && (
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Totals
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.phone && <TableCell />}
                    {visibleColumns.email && <TableCell />}
                    {visibleColumns.category && <TableCell />}
                    {visibleColumns.bills && <TableCell />}
                    {visibleColumns.dueDate && <TableCell />}
                    {visibleColumns.totalCredited && (
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>{fmt(totals.totalCredited)}</Typography>
                      </TableCell>
                    )}
                    {visibleColumns.totalPaid && (
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="success.main">{fmt(totals.totalPaid)}</Typography>
                      </TableCell>
                    )}
                    {visibleColumns.balance && (
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="error.main">{fmt(totals.balance)}</Typography>
                      </TableCell>
                    )}
                    {visibleColumns.status && <TableCell />}
                    {visibleColumns.actions && <TableCell />}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredCreditors.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>

      {/* Column Visibility Menu */}
      <Menu anchorEl={columnAnchor} open={Boolean(columnAnchor)} onClose={() => setColumnAnchor(null)}>
        {Object.keys(visibleColumns).map(col => (
          <MenuItem key={col} onClick={() => setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }))}>
            <Checkbox checked={visibleColumns[col]} size="small" />
            <ListItemText primary={col.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())} />
          </MenuItem>
        ))}
      </Menu>

      {/* View Dialog */}
      {selectedCreditor && (
        <CreditorViewDialog
          open={showViewDialog}
          creditor={selectedCreditor}
          onClose={() => { setShowViewDialog(false); setSelectedCreditor(null); }}
          onPayClick={() => { setShowViewDialog(false); setShowPaymentDialog(true); }}
        />
      )}

      {/* Payment Dialog */}
      {selectedCreditor && (
        <CreditorPaymentDialog
          open={showPaymentDialog}
          creditor={selectedCreditor}
          onClose={() => { setShowPaymentDialog(false); setSelectedCreditor(null); }}
          onSuccess={(msg) => showSnackbar(msg)}
          currentUser={currentUser}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Creditors;
